import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { type ComputedRef, type Ref, computed } from "vue";
import type { UserRolesResponse, RoleCredentials, RoleData } from "../../lib/dynamodb/types";
import type { CredentialsStorage } from "./useAuth";

export function getDynamoDBClient(role: RoleCredentials | null | undefined): DynamoDBClient | null {
	if (!role.accessKeyId || !role.secretAccessKey || !role.sessionToken) {
		return null;
	}

	return new DynamoDBClient({
		region: role.region,
		credentials: {
			accessKeyId: role.accessKeyId,
			secretAccessKey: role.secretAccessKey,
			sessionToken: role.sessionToken,
		},
	});
}

export const useDynamoDBClients = (
	credentials: Ref<CredentialsStorage | null>,
): Record<
	string,
	ComputedRef<{ user_table_name: string; cam_table_name: string; client: DynamoDBClient } | null>
> => {
	const getRoleData = (k: string) => (credentials.value?.creds?.roles as Record<string, RoleData> | undefined)?.[k];
	const getRoleDataWithClient = (k: string) => {
		const roleData = getRoleData(k);
		if (!roleData) {
			return null;
		}
		const client = getDynamoDBClient(roleData.role);
		if (!client) {
			return null;
		}
		return {
			user_table_name: roleData.user_table_name,
			cam_table_name: roleData.cam_table_name,
			client,
		};
	};

	return window.VERSION === "admin" ? {
		AdminPolicy: computed(() => getRoleDataWithClient("AdminPolicy")),
		ReadEventStats: computed(() => getRoleDataWithClient("ReadEventStats")),
	} : {
		ReadOwnUserData: computed(() => getRoleDataWithClient("ReadOwnUserData")),
		ReadAuthorizedProjectData: computed(() =>
			getRoleDataWithClient("ReadAuthorizedProjectData"),
		),
		QueryProjectMembersGSI: computed(() =>
			getRoleDataWithClient("QueryProjectMembersGSI"),
		),
		ReadAllCameras: computed(() => getRoleDataWithClient("ReadAllCameras")),
		QueryMapCamGSI: computed(() => getRoleDataWithClient("QueryMapCamGSI")),
		QueryEventsByProjectGSI: computed(() =>
			getRoleDataWithClient("QueryEventsByProjectGSI"),
		),
		ReadEventStats: computed(() => getRoleDataWithClient("ReadEventStats")),
	};
};
